import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Trash2 } from "lucide-react";
import QuestionModal from './QuestionModal';
import { useAllQuestions, useCurrentUserQuestions, usePostQuestion, useSearchQuestions, useDeleteQuestion } from '../../../../queries/professoru/forum.query';
import MusicLoader from '../../../../helper/MusicLoader';
import { useProfessorUActivityTracker } from '../../../../hooks/useProfessorUActivityTracker';

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
    });
};

const getQuestionTitle = (item) => {
    if (item.subject && item.question) {
        return `${item.question}`;
    }
    return item.title || item.question || item.subject || 'Untitled';
};

const QuestionRow = ({ item, onView, showDelete = false, onDeleteRequest, isDeleting = false }) => {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 bg-gradient-to-b from-[#6c76b5] via-[#3f4a9a] to-[#0b0f87] p-2 sm:p-3 rounded-lg">
            <span className="text-white text-xs sm:text-sm shrink-0">{formatDate(item.postedAt || item.date)}</span>

            <div className="flex-1 min-w-0">
                <div className="bg-gray-200 text-blue-900 text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full flex items-center gap-2">
                    <span className="truncate flex-1">{getQuestionTitle(item)}</span>
                    {item.isResolved && (
                        <div className="shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white">
                            <Check size={12} strokeWidth={3} />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2">
                {showDelete && !item.isResolved && (
                    <button
                        onClick={() => onDeleteRequest?.(item._id)}
                        disabled={isDeleting}
                        className="shrink-0 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed p-1"
                        title="Delete question"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
                <button onClick={() => onView(item)} className="bg-theme_clr2 hover:bg-orange-600 text-white px-3 sm:px-4 py-1 rounded-lg text-xs sm:text-sm whitespace-nowrap">
                    View
                </button>
            </div>
        </div>
    );
};

const Section = ({ title, data, sm = 66, lg = 68, isLoading = false, emptyMessage = null, rightElement = null, hasNextPage = false, isFetchingNextPage = false, fetchNextPage, showDelete = false, onDeleteRequest, isDeleting = false }) => {
    const [selected, setSelected] = useState(null);
    const scrollContainerRef = useRef(null);
    const sentinelRef = useRef(null);
    const hasData = data.length > 0;

    useEffect(() => {
        if (!sentinelRef.current || !scrollContainerRef.current || !hasNextPage || isFetchingNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { root: scrollContainerRef.current, threshold: 0 }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    return (
        <div className="flex flex-col flex-1">
            <div className="mb-3">
                {title && (
                    <div className="bg-gray-200 text-blue-900 px-3 py-2 rounded-md font-semibold relative text-sm sm:text-base w-full flex items-center justify-between gap-3">
                        <span className="shrink-0">{title}</span>
                        {rightElement}
                        <div className="absolute bottom-0 left-0 h-[2px] w-36 bg-theme_clr2"></div>
                    </div>
                )}
            </div>

            <div
                ref={scrollContainerRef}
                className="overflow-y-auto pr-2 flex-1"
                style={{ maxHeight: `${sm}vh` }}
            >
                {isLoading ? (
                    <div className="flex items-center justify-center min-h-[100px]">
                        <MusicLoader />
                    </div>
                ) : !hasData && emptyMessage ? (
                    <div className="flex items-center justify-center min-h-[100px] text-white text-sm">
                        {emptyMessage}
                    </div>
                ) : (
                    <>
                        {data.map((item, index) => (
                            <QuestionRow
                                key={item._id || index}
                                item={item}
                                onView={(item) => setSelected(item)}
                                showDelete={showDelete}
                                onDeleteRequest={onDeleteRequest}
                                isDeleting={isDeleting}
                            />
                        ))}
                        {isFetchingNextPage && (
                            <div className="flex items-center justify-center py-3">
                                <MusicLoader />
                            </div>
                        )}
                        <div ref={sentinelRef} className="h-1 w-full" />
                    </>
                )}
            </div>
            <QuestionModal
                isOpen={!!selected}
                data={selected}
                onClose={() => setSelected(null)}
            />
        </div>
    );
};

export default function Forum() {
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const { artistInfo } = useSelector(state => state.artistDetails);
    const { userDetails } = useSelector(state => state.auth);
    const currentUserId = userDetails?._id || userDetails?.userId;
    const queryClient = useQueryClient();
    const { trackQuestionPosted } = useProfessorUActivityTracker();
    const {
        data: questionsResponse,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending,
        refetch
    } = useAllQuestions();
    const {
        data: userQuestionsResponse,
        fetchNextPage: fetchNextUserPage,
        hasNextPage: hasNextUserPage,
        isFetchingNextPage: isFetchingNextUserPage,
        isPending: isUserPending,
        refetch: refetchUserQuestions,
    } = useCurrentUserQuestions({ enabled: !!currentUserId });
    const { mutate: postQuestion, isPending: isPosting } = usePostQuestion();
    const { mutate: deleteQuestion, isPending: isDeleting } = useDeleteQuestion();
    const {
        data: searchResponse,
        fetchNextPage: fetchNextSearchPage,
        hasNextPage: hasNextSearchPage,
        isFetchingNextPage: isFetchingNextSearchPage,
        isPending: isSearchPending,
    } = useSearchQuestions(searchQuery.trim(), { enabled: !!searchQuery.trim() });

    const questions = questionsResponse?.pages
        ? questionsResponse.pages.flatMap(page => Array.isArray(page) ? page : (page?.data || []))
        : (Array.isArray(questionsResponse) ? questionsResponse : (questionsResponse?.data || []));
    const yourQuestions = userQuestionsResponse?.pages
        ? userQuestionsResponse.pages.flatMap(page => Array.isArray(page) ? page : (page?.data || []))
        : (Array.isArray(userQuestionsResponse) ? userQuestionsResponse : (userQuestionsResponse?.data || []));

    const featuredQuestions = currentUserId
        ? questions.filter(q => q.userId !== currentUserId)
        : questions;

    const searchResults = searchResponse?.pages
        ? searchResponse.pages.flatMap(page => Array.isArray(page) ? page : (page?.data || []))
        : [];
    const isSearching = !!searchQuery.trim();
    const featuredSectionData = isSearching ? searchResults : featuredQuestions;
    const featuredSectionLoading = isSearching ? isSearchPending : isPending;
    const featuredSectionHasNextPage = isSearching ? hasNextSearchPage : hasNextPage;
    const featuredSectionIsFetchingNextPage = isSearching ? isFetchingNextSearchPage : isFetchingNextPage;
    const featuredSectionFetchNextPage = isSearching ? fetchNextSearchPage : fetchNextPage;

    const handleDeleteRequest = (id) => {
        setDeleteTargetId(id);
    };

    const handleConfirmDelete = () => {
        if (!deleteTargetId || isDeleting) return;
        deleteQuestion({ ids: [deleteTargetId] }, {
            onSuccess: () => {
                setDeleteTargetId(null);
                refetchUserQuestions();
                queryClient.invalidateQueries({ queryKey: ['forum', 'questions'] });
            },
        });
    };

    const handleCancelDelete = () => {
        setDeleteTargetId(null);
    };

    const handlePostQuestion = () => {
        if (!subject.trim() || !message.trim() || isPosting) return;

        postQuestion({ subject: subject.trim().charAt(0).toUpperCase() + subject.trim().slice(1), question: message.trim() }, {
            onSuccess: (data) => {
                setSubject("");
                setMessage("");
                queryClient.invalidateQueries({ queryKey: ['forum', 'questions'] });
                refetch();
                refetchUserQuestions();
                trackQuestionPosted(data?._id || data?.data?._id);
            },
        });
    };

    const isPostDisabled = isPosting || !subject.trim() || !message.trim();

    return (
        <div className="info-box min-h-full bg-theme_clr1 flex flex-col rounded-xl gap-4 p-3 sm:p-4 lg:p-5 mt-4">
            <div className="header-section w-full flex flex-wrap items-center justify-between gap-2 text-white">
                <div className="title-info text-xl sm:text-2xl lg:text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
                    <img src={require('../../../../assets/images/admin/Amplitude-Info.png')} alt="Amplitude Info" className='w-[24px] h-[24px] sm:w-[30px] sm:h-[30px]' /> Forum
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                <div className="grid-item">
                    <div className="flex flex-col flex-1">

                        {/* Post Question */}
                        <div className="mb-6 bg-gradient-to-b from-[#6c76b5] via-[#3f4a9a] to-[#0b0f87] sm:bg-none p-3 sm:p-0 rounded-lg" data-tour='post-questions'>

                            {/* Title */}
                            <div className="bg-gray-200 text-blue-900 px-3 py-2 rounded-md font-semibold mb-4 relative text-sm sm:text-base">
                                Post a question
                                <div className="absolute bottom-0 left-0 h-[2px] w-32 bg-theme_clr2"></div>
                            </div>

                            {/* SUBJECT INPUT */}
                            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 mb-4">

                                <label className="text-white sm:text-white text-sm shrink-0">
                                    Subject:
                                </label>

                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Enter subject..."
                                    className="w-full sm:flex-1 min-w-0 bg-gray-200 rounded-md px-3 py-2 text-theme_clr1 outline-none text-sm placeholder:text-theme_clr1
          text-sm"
                                />

                                {/* Desktop Button (unchanged) */}
                                <button
                                    onClick={handlePostQuestion}
                                    disabled={isPostDisabled}
                                    className="hidden sm:block bg-theme_clr2 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-md text-white text-sm"
                                >
                                    {isPosting ? 'Posting...' : 'Post Question'}
                                </button>
                            </div>

                            {/* MESSAGE ROW */}
                            <div className="flex items-center md:bg-gradient-to-b from-[#6c76b5] via-[#3f4a9a] to-[#0b0f87] md:p-3 gap-2 sm:gap-3 mb-3 sm:mb-0">

                                {/* Profile */}
                                <div className="flex flex-col items-center gap-1 shrink-0">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 rounded-md overflow-hidden">
                                        <img
                                            src={artistInfo?.avatar}
                                            alt="user"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <p className="text-white text-xs sm:text-sm">{artistInfo?.name}</p>
                                </div>

                                {/* Input */}
                                <input
                                    type="text"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder={`Hey this is ${artistInfo?.name}, I wanted to ask about....`}
                                    className="
          flex-1 min-w-0
          bg-gray-200 text-theme_clr1
          px-3 sm:px-4 py-2 sm:py-3
          rounded-full outline-none
          placeholder:text-theme_clr1
          text-sm
        "
                                />
                            </div>

                            {/* MOBILE BUTTON */}
                            <button
                                onClick={handlePostQuestion}
                                disabled={isPostDisabled}
                                className="sm:hidden w-full mt-2 bg-theme_clr2 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-md text-white text-sm"
                            >
                                {isPosting ? 'Posting...' : 'Post Question'}
                            </button>
                        </div>

                        {/* Your Questions */}
                        <div data-tour='your-questions'>
                            <Section
                                title="Your Questions"
                                data={yourQuestions}
                                sm={27}
                                lg={32}
                                isLoading={isUserPending}
                                emptyMessage="No Questions posted yet"
                                hasNextPage={hasNextUserPage}
                                isFetchingNextPage={isFetchingNextUserPage}
                                fetchNextPage={fetchNextUserPage}
                                showDelete={true}
                                onDeleteRequest={handleDeleteRequest}
                                isDeleting={isDeleting}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid-item">
                    <div className="flex flex-col flex-1">
                        <div className="flex flex-col flex-1" data-tour='featured-questions'>
                            <Section
                                title="Featured Questions"
                                data={featuredSectionData}
                                isLoading={featuredSectionLoading}
                                hasNextPage={featuredSectionHasNextPage}
                                isFetchingNextPage={featuredSectionIsFetchingNextPage}
                                fetchNextPage={featuredSectionFetchNextPage}
                                rightElement={
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search..."
                                        className="w-full max-w-[160px] bg-gradient-to-b from-[#6c76b5] via-[#3f4a9a] to-[#0b0f87] text-white placeholder:text-white/70 px-4 py-2 rounded-full outline-none border border-white/20 text-sm"
                                    />
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteTargetId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
                    <div className="w-full max-w-sm bg-white rounded-lg p-5 shadow-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Question</h3>
                        <p className="text-sm text-gray-600 mb-5">Are you sure you want to delete this question?</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}